import type {
  AssignPlanAssetsRequestDto,
  AttachPlanTaskRequestDto,
  CheckWizardAssignmentConflictsRequestDto,
  CreatePartRequestDto,
  CreatePlanFromWizardRequestDto,
  CreateTaskRequestDto,
  CreateTriggerRequestDto,
  ListAssignmentConflictsResponseDto,
  ListPartsResponseDto,
  ListPlanTasksResponseDto,
  ListPlansResponseDto,
  ListTasksResponseDto,
  ListTriggersResponseDto,
  ListWizardFilterOptionsResponseDto,
  ListWorkTypesResponseDto,
  PartResponseDto,
  PlanResponseDto,
  PlanTaskResponseDto,
  PreviewPlanFromWizardRequestDto,
  PreviewPlanFromWizardResponseDto,
  ReorderPlanTasksRequestDto,
  TaskResponseDto,
  TriggerResponseDto,
  UpdatePartRequestDto,
  UpdatePlanRequestDto,
  UpdateTaskRequestDto,
  UpdateTriggerRequestDto,
  PreviewAssignmentConflictsRequestDto,
  WizardFilterOptionDto,
} from '@evimo/client/models';
import { ListWizardFilterOptionsResponseDtoFromJSON } from '@evimo/client/models';
import { buildApiUrl } from '~/app/lib/api-config';
import {
  partsApi,
  planAssetsApi,
  planTasksApi,
  plansApi,
  pmRequestInit,
  tasksApi,
  triggersApi,
  workTypesApi,
} from '~/app/services/preventive-maintenance/preventive-maintenance-api-instances';
import {
  buildWizardAssignableAssetsSearchParams,
  type WizardAssignableAssetFilters,
  type WizardAssignableAssetsPagination,
  WIZARD_ASSIGNABLE_ASSETS_PAGE_SIZE,
} from '~/app/services/preventive-maintenance/wizard-assignable-assets.params';

export type { WizardAssignableAssetFilters, WizardAssignableAssetsPagination };
export {
  buildWizardAssignableAssetsQueryKey,
  buildWizardAssignableAssetsSearchParams,
  WIZARD_ASSIGNABLE_ASSETS_PAGE_SIZE,
} from '~/app/services/preventive-maintenance/wizard-assignable-assets.params';

/** Default page size for Plan Detail Assigned assets / Schedules tables. */
export const PLAN_DETAIL_TABLE_PAGE_SIZE = 10;

export type PlanDetailTablePagination = {
  page: number;
  limit: number;
};

export type PlanDetailPaginationMeta = {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type PlanAssetListItem = {
  id: string;
  assetId: string;
  assetName: string;
  serialNumber?: string | null;
  siteName?: string | null;
  active: boolean;
  startDate: string | Date;
  assignedAt: string | Date;
};

export type ScheduleListItem = {
  id: string;
  assetId: string;
  assetName: string;
  serialNumber?: string | null;
  triggerId: string;
  active: boolean;
  nextDueAt?: string | Date | null;
  nextDueReading?: number | null;
};

export type TaskSubtaskDto = {
  id: string;
  name: string;
  sequence: number;
};

export type TaskSubtaskInput = {
  name: string;
  sequence?: number;
};

export type CreateTaskBody = CreateTaskRequestDto;
export type UpdateTaskBody = UpdateTaskRequestDto;

export type WizardFilterOption = WizardFilterOptionDto;
export type ListWizardFilterOptionsResponse =
  ListWizardFilterOptionsResponseDto;
export type CheckWizardAssignmentConflictsRequest =
  CheckWizardAssignmentConflictsRequestDto;

async function fetchPmJson<T>(
  path: string,
  fromJson: (json: unknown) => T,
): Promise<T> {
  const response = await fetch(buildApiUrl(path), {
    credentials: 'include',
  });
  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed (${response.status})`);
  }
  return fromJson(await response.json());
}

function hasNonEmptyIds(ids?: string[]): boolean {
  return Array.isArray(ids) && ids.length > 0;
}

function appendIdParams(
  params: URLSearchParams,
  key: string,
  ids?: string[],
): void {
  for (const id of ids ?? []) {
    if (id) params.append(key, id);
  }
}

export const PreventiveMaintenanceService = {
  // Plans
  listPlans(): Promise<ListPlansResponseDto> {
    return plansApi.planControllerFindAll(pmRequestInit);
  },

  getPlanById(id: string): Promise<PlanResponseDto> {
    return plansApi.planControllerFindById({ id }, pmRequestInit);
  },

  updatePlan(id: string, dto: UpdatePlanRequestDto): Promise<PlanResponseDto> {
    return plansApi.planControllerUpdate(
      { id, updatePlanRequestDto: dto },
      pmRequestInit,
    );
  },

  createPlanFromWizard(
    dto: CreatePlanFromWizardRequestDto,
  ): Promise<PlanResponseDto> {
    return plansApi.planControllerCreateFromWizard(
      { createPlanFromWizardRequestDto: dto },
      pmRequestInit,
    );
  },

  previewPlanFromWizard(
    dto: PreviewPlanFromWizardRequestDto,
  ): Promise<PreviewPlanFromWizardResponseDto> {
    return plansApi.planControllerPreviewFromWizard(
      { previewPlanFromWizardRequestDto: dto },
      pmRequestInit,
    );
  },

  /**
   * Wizard Step 3 assignable assets (no planId) — always send page/limit.
   * OpenAPI client may omit filter/pagination query params — use fetch.
   */
  async findWizardAssignableAssets(
    filters?: WizardAssignableAssetFilters,
    pagination: WizardAssignableAssetsPagination = {
      page: 1,
      limit: WIZARD_ASSIGNABLE_ASSETS_PAGE_SIZE,
    },
  ): Promise<{
    data: Array<{
      id: string;
      name: string;
      serialNumber: string;
      zoneName?: string | null;
      siteName: string;
    }>;
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const params = buildWizardAssignableAssetsSearchParams(filters, pagination);
    const response = await fetch(
      buildApiUrl(
        `/api/v1/preventive-maintenance/plans/wizard/assignable-assets?${params.toString()}`,
      ),
      { credentials: 'include' },
    );

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || `Request failed (${response.status})`);
    }

    const json = (await response.json()) as {
      data?: unknown;
      meta?: {
        total?: number;
        page?: number;
        limit?: number;
        totalPages?: number;
      };
    };

    const data = Array.isArray(json.data) ? json.data : [];
    const total = Number(json.meta?.total ?? data.length);
    const page = Number(json.meta?.page ?? pagination.page);
    const limit = Number(json.meta?.limit ?? pagination.limit);
    const totalPages =
      Number(json.meta?.totalPages) ||
      (limit > 0 ? Math.ceil(total / limit) : 0);

    return {
      data: data as Array<{
        id: string;
        name: string;
        serialNumber: string;
        zoneName?: string | null;
        siteName: string;
      }>,
      meta: { total, page, limit, totalPages },
    };
  },

  /**
   * Ids-only list for wizard “Select all matching” (same filters as assignable-assets).
   */
  async findWizardAssignableAssetIds(
    filters?: WizardAssignableAssetFilters,
  ): Promise<string[]> {
    const params = buildWizardAssignableAssetsSearchParams(filters, {
      page: 1,
      limit: 1,
    });
    // Drop pagination — ids endpoint uses filters-only DTO.
    params.delete('page');
    params.delete('limit');

    const response = await fetch(
      buildApiUrl(
        `/api/v1/preventive-maintenance/plans/wizard/assignable-assets/ids?${params.toString()}`,
      ),
      { credentials: 'include' },
    );

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || `Request failed (${response.status})`);
    }

    const json = (await response.json()) as { data?: unknown };
    return Array.isArray(json.data)
      ? json.data.filter((id): id is string => typeof id === 'string')
      : [];
  },

  checkWizardConflicts(
    dto: CheckWizardAssignmentConflictsRequest,
  ): Promise<ListAssignmentConflictsResponseDto> {
    return plansApi.planControllerCheckWizardConflicts(
      { checkWizardAssignmentConflictsRequestDto: dto },
      pmRequestInit,
    );
  },

  listWizardFilterLandlords(): Promise<ListWizardFilterOptionsResponse> {
    return plansApi.planControllerFindWizardFilterLandlords(pmRequestInit);
  },

  listWizardFilterAssetTypes(): Promise<ListWizardFilterOptionsResponse> {
    return plansApi.planControllerFindWizardFilterAssetTypes(pmRequestInit);
  },

  /**
   * OpenAPI client omits optional `assetTypeIds` query — use fetch when set.
   */
  listWizardFilterAssetModels(
    assetTypeIds?: string[],
  ): Promise<ListWizardFilterOptionsResponse> {
    if (!hasNonEmptyIds(assetTypeIds)) {
      return plansApi.planControllerFindWizardFilterAssetModels(pmRequestInit);
    }
    const params = new URLSearchParams();
    appendIdParams(params, 'assetTypeIds', assetTypeIds);
    return fetchPmJson(
      `/api/v1/preventive-maintenance/plans/wizard/filter-options/asset-models?${params.toString()}`,
      ListWizardFilterOptionsResponseDtoFromJSON,
    );
  },

  listWizardFilterSites(): Promise<ListWizardFilterOptionsResponse> {
    return plansApi.planControllerFindWizardFilterSites(pmRequestInit);
  },

  // Tasks
  listTasks(): Promise<ListTasksResponseDto> {
    return tasksApi.taskControllerFindAll(pmRequestInit);
  },

  getTaskById(id: string): Promise<TaskResponseDto> {
    return tasksApi.taskControllerFindById({ id }, pmRequestInit);
  },

  createTask(dto: CreateTaskRequestDto): Promise<TaskResponseDto> {
    return tasksApi.taskControllerCreate(
      { createTaskRequestDto: dto },
      pmRequestInit,
    );
  },

  updateTask(id: string, dto: UpdateTaskRequestDto): Promise<TaskResponseDto> {
    return tasksApi.taskControllerUpdate(
      { id, updateTaskRequestDto: dto },
      pmRequestInit,
    );
  },

  // Triggers
  listTriggers(): Promise<ListTriggersResponseDto> {
    return triggersApi.triggerControllerFindAll(pmRequestInit);
  },

  getTriggerById(id: string): Promise<TriggerResponseDto> {
    return triggersApi.triggerControllerFindById({ id }, pmRequestInit);
  },

  createTrigger(dto: CreateTriggerRequestDto): Promise<TriggerResponseDto> {
    return triggersApi.triggerControllerCreate(
      { createTriggerRequestDto: dto },
      pmRequestInit,
    );
  },

  updateTrigger(
    id: string,
    dto: UpdateTriggerRequestDto,
  ): Promise<TriggerResponseDto> {
    return triggersApi.triggerControllerUpdate(
      { id, updateTriggerRequestDto: dto },
      pmRequestInit,
    );
  },

  deleteTrigger(id: string): Promise<void> {
    return triggersApi.triggerControllerDelete({ id }, pmRequestInit);
  },

  // Work Types
  listWorkTypes(): Promise<ListWorkTypesResponseDto> {
    return workTypesApi.workTypeControllerFindAll(pmRequestInit);
  },

  getWorkTypeById(
    id: string,
  ): Promise<import('@evimo/client/models').WorkTypeResponseDto> {
    return workTypesApi.workTypeControllerFindById({ id }, pmRequestInit);
  },

  // Parts
  listParts(
    page: number,
    limit: number,
    q?: string,
  ): Promise<ListPartsResponseDto> {
    return partsApi.partControllerFindAll({ page, limit, q }, pmRequestInit);
  },

  getPartById(id: string): Promise<PartResponseDto> {
    return partsApi.partControllerFindById({ id }, pmRequestInit);
  },

  createPart(dto: CreatePartRequestDto): Promise<PartResponseDto> {
    return partsApi.partControllerCreate(
      { createPartRequestDto: dto },
      pmRequestInit,
    );
  },

  updatePart(id: string, dto: UpdatePartRequestDto): Promise<PartResponseDto> {
    return partsApi.partControllerUpdate(
      { id, updatePartRequestDto: dto },
      pmRequestInit,
    );
  },

  // Plan Tasks
  listPlanTasks(planId: string): Promise<ListPlanTasksResponseDto> {
    return planTasksApi.planTaskControllerFindAll({ planId }, pmRequestInit);
  },

  attachPlanTask(
    planId: string,
    dto: AttachPlanTaskRequestDto,
  ): Promise<PlanTaskResponseDto> {
    return planTasksApi.planTaskControllerAttach(
      { planId, attachPlanTaskRequestDto: dto },
      pmRequestInit,
    );
  },

  reorderPlanTasks(
    planId: string,
    dto: ReorderPlanTasksRequestDto,
  ): Promise<ListPlanTasksResponseDto> {
    return planTasksApi.planTaskControllerReorder(
      { planId, reorderPlanTasksRequestDto: dto },
      pmRequestInit,
    );
  },

  detachPlanTask(planId: string, taskId: string): Promise<void> {
    return planTasksApi.planTaskControllerDetach(
      { planId, taskId },
      pmRequestInit,
    );
  },

  // Plan Assets
  listPlanAssets(
    planId: string,
    pagination: PlanDetailTablePagination = {
      page: 1,
      limit: PLAN_DETAIL_TABLE_PAGE_SIZE,
    },
  ): Promise<{
    data: PlanAssetListItem[];
    meta: PlanDetailPaginationMeta;
  }> {
    const params = new URLSearchParams();
    params.set('page', String(pagination.page));
    params.set('limit', String(pagination.limit));
    return fetch(
      buildApiUrl(
        `/api/v1/preventive-maintenance/plans/${planId}/assets?${params.toString()}`,
      ),
      { credentials: 'include' },
    ).then(async (response) => {
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `Request failed (${response.status})`);
      }
      const json = (await response.json()) as {
        data?: unknown;
        meta?: Partial<PlanDetailPaginationMeta>;
      };
      const data = Array.isArray(json.data)
        ? (json.data as PlanAssetListItem[])
        : [];
      const total = Number(json.meta?.total ?? data.length);
      const page = Number(json.meta?.page ?? pagination.page);
      const limit = Number(json.meta?.limit ?? pagination.limit);
      const totalPages =
        Number(json.meta?.totalPages) ||
        (limit > 0 ? Math.ceil(total / limit) : 0);
      return { data, meta: { total, page, limit, totalPages } };
    });
  },

  assignPlanAssets(
    planId: string,
    dto: AssignPlanAssetsRequestDto,
  ): Promise<{ data: unknown[] }> {
    return planAssetsApi.planAssetControllerAssign(
      { planId, assignPlanAssetsRequestDto: dto },
      pmRequestInit,
    );
  },

  findAssignableAssets(
    planId: string,
    pagination: WizardAssignableAssetsPagination = {
      page: 1,
      limit: WIZARD_ASSIGNABLE_ASSETS_PAGE_SIZE,
    },
  ): Promise<{
    data: Array<{
      id: string;
      name: string;
      serialNumber: string;
      zoneName?: string | null;
      siteName: string;
    }>;
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();
    params.set('page', String(pagination.page));
    params.set('limit', String(pagination.limit));
    return fetch(
      buildApiUrl(
        `/api/v1/preventive-maintenance/plans/${planId}/assets/assignable?${params.toString()}`,
      ),
      { credentials: 'include' },
    ).then(async (response) => {
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `Request failed (${response.status})`);
      }
      const json = (await response.json()) as {
        data?: unknown;
        meta?: {
          total?: number;
          page?: number;
          limit?: number;
          totalPages?: number;
        };
      };
      const data = Array.isArray(json.data) ? json.data : [];
      const total = Number(json.meta?.total ?? data.length);
      const page = Number(json.meta?.page ?? pagination.page);
      const limit = Number(json.meta?.limit ?? pagination.limit);
      const totalPages =
        Number(json.meta?.totalPages) ||
        (limit > 0 ? Math.ceil(total / limit) : 0);
      return {
        data: data as Array<{
          id: string;
          name: string;
          serialNumber: string;
          zoneName?: string | null;
          siteName: string;
        }>,
        meta: { total, page, limit, totalPages },
      };
    });
  },

  previewConflicts(
    planId: string,
    dto: PreviewAssignmentConflictsRequestDto,
  ): Promise<ListAssignmentConflictsResponseDto> {
    return planAssetsApi.planAssetControllerPreviewConflicts(
      { planId, previewAssignmentConflictsRequestDto: dto },
      pmRequestInit,
    );
  },

  // Schedules
  listSchedules(
    planId: string,
    pagination: PlanDetailTablePagination = {
      page: 1,
      limit: PLAN_DETAIL_TABLE_PAGE_SIZE,
    },
  ): Promise<{
    data: ScheduleListItem[];
    meta: PlanDetailPaginationMeta;
  }> {
    const params = new URLSearchParams();
    params.set('page', String(pagination.page));
    params.set('limit', String(pagination.limit));
    return fetch(
      buildApiUrl(
        `/api/v1/preventive-maintenance/plans/${planId}/schedules?${params.toString()}`,
      ),
      { credentials: 'include' },
    ).then(async (response) => {
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `Request failed (${response.status})`);
      }
      const json = (await response.json()) as {
        data?: unknown;
        meta?: Partial<PlanDetailPaginationMeta>;
      };
      const data = Array.isArray(json.data)
        ? (json.data as ScheduleListItem[])
        : [];
      const total = Number(json.meta?.total ?? data.length);
      const page = Number(json.meta?.page ?? pagination.page);
      const limit = Number(json.meta?.limit ?? pagination.limit);
      const totalPages =
        Number(json.meta?.totalPages) ||
        (limit > 0 ? Math.ceil(total / limit) : 0);
      return { data, meta: { total, page, limit, totalPages } };
    });
  },
};
